/**
 * Zod schema for the polymorphic `Media` persistence row.
 *
 * Mirrors `../types/media.ts`. Spec authority: `architecture.md §4.7`
 * `CREATE TABLE media`. Polymorphic parent-existence is enforced by the
 * application layer (architecture §4.22, §4.9), not by this schema.
 */

import { z } from "zod";
import type { Media, MediaKind, MediaParentKind } from "../types/media.js";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

export const MediaParentKindSchema: z.ZodType<MediaParentKind> = z.enum([
  "attempt",
  "artefact",
  "batch",
]);

export const MediaKindSchema: z.ZodType<MediaKind> = z.enum([
  "photo",
  "video",
  "audio",
  "document",
]);

export const MediaSchema = z
  .object({
    uuid,
    parentKind: MediaParentKindSchema,
    parentUuid: uuid,
    kind: MediaKindSchema,
    role: z.string().nullable().optional(),
    sha256: z.string(),
    captureTs: timestamp.nullable().optional(),
    width: z.number().int().nonnegative().nullable().optional(),
    height: z.number().int().nonnegative().nullable().optional(),
    r2OriginalKey: z.string(),
    createdAt: timestamp,
    /** Revocation epoch for HMAC download tokens (architecture.md §4.8). */
    mediaTokenEpoch: z.number().int().nonnegative(),
    /** True once server-side EXIF strip is verified (architecture.md §4.9). */
    exifStripped: z.boolean(),
    /** True if derivative processing permanently failed; user must re-upload. */
    processingFailed: z.boolean(),
  })
  .strict();

type _Assert =
  z.infer<typeof MediaSchema> extends Media
    ? Media extends z.infer<typeof MediaSchema>
      ? true
      : never
    : never;
export type __MediaSchemaAssert = _Assert;
